import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FcGoogle } from 'react-icons/fc';

const getSafeRedirectPath = (search) => {
  const params = new URLSearchParams(search);
  const redirect = params.get('redirect');

  if (
    !redirect ||
    !redirect.startsWith('/') ||
    redirect.startsWith('//') ||
    redirect.startsWith('/login')
  ) {
    return '/MemberPage';
  }

  return redirect;
};

// 定義內聯樣式
const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 'calc(100vh - 37px)',
    height: 'calc(100vh - 37px)',
    padding: '20px',
    marginTop: 0,
    backgroundColor: '#f8f9fa',
    position: 'fixed',
    top: '37px',
    left: 0,
    right: 0,
    bottom: 0,
    overflowY: 'auto',
  },
  darkContainer: {
    backgroundColor: '#1e1e1e', // 暗黑模式的背景顏色
  },
  card: {
    width: '90%',
    maxWidth: '380px',
    backgroundColor: 'white',
    borderRadius: '10px',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
    padding: '30px',
    transition: 'transform 0.3s ease',
    position: 'relative', // 用於放置返回按鈕
  },
  header: {
    textAlign: 'center',
    marginBottom: '25px',
  },
  title: {
    color: '#333',
    fontSize: '26px',
    marginBottom: '8px',
    fontWeight: 600,
  },
  subtitle: {
    color: '#666',
    fontSize: '15px',
  },
  error: {
    backgroundColor: '#fff0f0',
    color: '#e74c3c',
    padding: '10px',
    borderRadius: '5px',
    marginBottom: '15px',
    fontSize: '14px',
    borderLeft: '4px solid #e74c3c',
    width: '85%',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '18px',
    width: '100%',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    width: '85%',
    maxWidth: '300px',
    gap: '6px',
  },
  label: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#333',
    alignSelf: 'flex-start',
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid #ddd',
    borderRadius: '5px',
    fontSize: '15px',
    transition: 'border-color 0.3s',
  },
  formActions: {
    width: '85%',
    maxWidth: '300px',
    marginTop: '5px',
  },
  button: {
    width: '100%',
    padding: '11px',
    backgroundColor: '#8e7a55',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    fontSize: '15px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background-color 0.3s',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '85%',
    maxWidth: '300px',
    marginTop: '15px',
    fontSize: '13px',
  },
  link: {
    color: '#8e7a55',
    textDecoration: 'none',
    transition: 'color 0.3s',
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
    cursor: 'pointer',
  },
  backButton: {
    position: 'absolute',
    top: '15px',
    left: '15px',
    background: 'none',
    border: 'none',
    color: '#8e7a55',
    fontSize: '14px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    padding: '5px',
  },
  backIcon: {
    marginRight: '5px',
  },
};

const LoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectPath = getSafeRedirectPath(location.search);

  // 檢測暗黑模式
  const [isDarkMode, setIsDarkMode] = useState(
    localStorage.getItem('darkMode') === 'true'
  );

  // 監聽暗黑模式的變化
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(localStorage.getItem('darkMode') === 'true');
    };

    // 初始檢查
    checkDarkMode();

    // 監聽 storage 變化
    window.addEventListener('storage', checkDarkMode);

    return () => {
      window.removeEventListener('storage', checkDarkMode);
    };
  }, []);

  // 初始化表單數據
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });

  // 錯誤訊息
  const [error, setError] = useState('');

  // 處理輸入框
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  // 送出表單
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.username || !formData.password) {
      setError(t('login.errorEmptyFields', '請填寫用戶名和密碼'));
      return;
    }

    try {
      const response = await axios.post('/api/denglu', formData, {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true,
      });
      console.log('登入成功回應：', response.data);
      navigate(redirectPath, { replace: true });
    } catch (err) {
      setError(t('login.errorLogin', '登入失敗，請檢查用戶名和密碼'));
    }
  };

  // 固定返回首頁，避免登入頁之間的歷史紀錄造成返回 loop
  const handleGoBack = () => {
    navigate('/');
  };

  // 忘記密碼
  const handleForgotPassword = (e) => {
    e.preventDefault();
    navigate('/verifycode'); // ✅ 導向驗證頁
  };

  // 點擊「註冊新帳號」
  const handleRegister = (e) => {
    e.preventDefault();
    navigate('/register');
  };

  // 點擊 Google 登入按鈕
  const handleGoogleLogin = () => {
    window.location.href = `/api/auth/google?redirect=${encodeURIComponent(
      redirectPath
    )}`;
  };

  // 組合容器樣式
  const containerStyle = {
    ...styles.container,
    ...(isDarkMode ? styles.darkContainer : {}),
  };

  return (
    <div style={containerStyle}>
      <div style={styles.card}>
        <button onClick={handleGoBack} style={styles.backButton}>
          <span style={styles.backIcon}>←</span>
          {t('login.home', 'Home')}
        </button>

        <div style={styles.header}>
          <h1 style={styles.title}>{t('login.title', '歡迎登入')}</h1>
          <p style={styles.subtitle}>
            {t('login.subtitle', '請輸入您的帳號和密碼')}
          </p>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formGroup}>
            <label htmlFor="username" style={styles.label}>
              {t('login.username', '用戶名')}
            </label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              placeholder={t('login.usernamePlaceholder', '請輸入用戶名')}
              style={styles.input}
            />
          </div>

          <div style={styles.formGroup}>
            <label htmlFor="password" style={styles.label}>
              {t('login.password', '密碼')}
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder={t('login.passwordPlaceholder', '請輸入密碼')}
              style={styles.input}
            />
          </div>

          <div style={styles.formActions}>
            <button type="submit" style={styles.button}>
              {t('login.loginButton', '登入')}
            </button>
          </div>

          {/* 新增 Google 登入按鈕 */}
          <div style={{ ...styles.formActions, marginTop: '10px' }}>
            <button
              type="button"
              onClick={handleGoogleLogin}
              style={{
                ...styles.button,
                backgroundColor: '#4225F4',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px', // 圖標和文字間增加間距
              }}
            >
              <FcGoogle size={22} />
              {t('login.googleLogin', '使用 Google 登入')}
            </button>
          </div>

          <div style={styles.footer}>
            <button
              type="button"
              onClick={handleForgotPassword}
              style={styles.link}
            >
              {t('login.forgotPassword', '忘記密碼？')}
            </button>
            <button type="button" onClick={handleRegister} style={styles.link}>
              {t('login.register', '註冊新帳號')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
